// #4294 / #4310 — Reusable column-visibility state with userSettings
// persistence (extracted from performance-v2.tsx, #4932 Phase 0.4).
//
// Owns one table's visible-column Set: initialises to the default set,
// rehydrates once from userSettings.performance.column_visibility[key],
// and writes through on every change. Pinned columns can never be hidden.
// Returns plain data + callbacks (never components — see the remount
// pitfall with hooks that return renderables).
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { userSettingsQueryOptions, useUpdateUserSettingsMutation } from '@/lib/query-options';

export function useColumnVisibility({
  settingsKey,
  allIds,
  pinnedIds,
  defaultVisibleIds,
}: {
  /** Key under userSettings.performance.column_visibility (e.g. 'campaigns'). */
  settingsKey: string;
  allIds: Set<string>;
  pinnedIds: Set<string>;
  /** Initial + reset target; defaults to allIds (everything visible). */
  defaultVisibleIds?: Set<string>;
}): {
  visible: Set<string>;
  toggle: (id: string) => void;
  reset: () => void;
  /** Replace the whole set (e.g. applying a saved view) and persist it. */
  apply: (ids: Set<string>) => void;
} {
  const defaults = defaultVisibleIds ?? allIds;
  const { data: userSettings } = useQuery(userSettingsQueryOptions());
  const settingsMutation = useUpdateUserSettingsMutation();

  const [visible, setVisible] = useState<Set<string>>(() => new Set(defaults));

  // Rehydrate once from persisted settings. Force-restores pinned ids so the
  // table can never render without its label column. Newly-added columns
  // (not yet present in the saved set) default to visible so returning users
  // see them — opting out of new columns silently would be a bad surprise.
  const loaded = useRef(false);
  useEffect(() => {
    if (!userSettings || loaded.current) return;
    const saved = userSettings.performance?.column_visibility?.[settingsKey];
    if (Array.isArray(saved)) {
      const restored = new Set<string>(saved.filter(id => allIds.has(id)));
      const savedSet = new Set(saved);
      for (const id of allIds) {
        if (!savedSet.has(id)) restored.add(id);
      }
      for (const id of pinnedIds) restored.add(id);
      setVisible(restored);
    }
    loaded.current = true;
  }, [userSettings, settingsKey]);

  const persist = useCallback((next: Set<string>) => {
    // Save immediately — this is rare-frequency user input, no need to debounce.
    settingsMutation.mutate({
      performance: { column_visibility: { [settingsKey]: Array.from(next) } },
    });
  }, [settingsMutation, settingsKey]);

  // Toggle one column and persist. Pinned columns can't be hidden — the
  // request to remove one is a no-op.
  const toggle = useCallback((id: string) => {
    if (pinnedIds.has(id)) return;
    setVisible(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      persist(next);
      return next;
    });
  }, [persist]);

  const reset = useCallback(() => {
    const next = new Set(defaults);
    setVisible(next);
    persist(next);
  }, [persist]);

  const apply = useCallback((ids: Set<string>) => {
    setVisible(ids);
    persist(ids);
  }, [persist]);

  return { visible, toggle, reset, apply };
}
